import {
  createContext,
  useContext,
  useState,
  useRef,
  RefObject,
  FC,
} from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
} from '@chakra-ui/react';

interface IUseDialog {
  createDialog: (dialog: DialogProps) => void;
  closeDialog: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

const DialogContext = createContext({
  createDialog: (_dialog: DialogProps) => {},
  closeDialog: () => {},
  loading: false,
});

interface DialogProps {
  title: string;
  content: string;
  cancelText?: string;
  confirmText?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const DialogProvider: FC<any> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogProps | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const createDialog = (newDialog: DialogProps | null) => {
    setDialog(newDialog);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const onCloseHandler = () => {
    dialog?.onCancel();
    closeDialog();
  };

  const onConfirmHandler = async () => {
    await dialog?.onConfirm();
  };

  const contextValue = useRef({createDialog, closeDialog, loading, setLoading});
  const cancelRef = useRef() as RefObject<HTMLElement | HTMLButtonElement>

  return (
    <DialogContext.Provider value={contextValue.current}>
      {children}
      <AlertDialog
        isOpen={open}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>{dialog?.title}</AlertDialogHeader>
            <AlertDialogBody>{dialog?.content}</AlertDialogBody>

            <AlertDialogFooter>
              <Button
                onClick={onCloseHandler}
                isDisabled={loading}
              >
                {dialog?.cancelText || 'Cancelar'}
              </Button>
              <Button
                colorScheme='brand'
                onClick={onConfirmHandler} ml={3}
                isLoading={loading}
              >
                {dialog?.confirmText || 'Confirm'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DialogContext.Provider>
  );
};

export const useDialog = (): IUseDialog => {
  const result = useContext(DialogContext);
  if (!result) {
    throw new Error('Dialog context is only available inside its provider');
  }
  return result as IUseDialog;
};

export default DialogProvider;
